import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Linkedin, Newspaper, Mail, Megaphone,
  Lightbulb, Compass, Search, Blocks, RefreshCw,
} from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton } from "@/components/marketing";
import { SOLUTIONS_BY_SLUG, BOOK_HREF } from "@/data/solutions";

const TEAL = "#22D3C5";
const EASE = [0.22, 1, 0.36, 1];

const HOME_DESC = {
  "aicom-ai": "Din digitale kommunikationsrådgiver, der kender jer indgående, forstår hvad I bør sige og producerer professionelt indhold på minutter.",
  "ai-avatar": "Få en digital avatar - det velkendte ansigt på alt jeres kommunikation.",
  "paid-social": "Målrettede annoncer på LinkedIn, Facebook og Instagram, der forvandler visninger til leads og salg.",
  "seo": "Bliv fundet af de kunder, der allerede søger efter det, I tilbyder - og få flere henvendelser.",
  "thought-leadership": "Bliv den virksomhed, kunder, medier og beslutningstagere lytter til.",
  "strategi": "Den røde tråd mellem forretning, brand og kommunikation - så alt, I gør, trækker i samme retning.",
  "hjemmesider": "Moderne hjemmesider, hvor strategi, design, AI og konvertering går hånd i hånd.",
  "ai-agenter": "En intelligent digital kollega, der besvarer spørgsmål, analyserer dokumenter og automatiserer processer - døgnet rundt.",
  "ai-implementering": "Vi identificerer, implementerer og automatiserer - så AI bliver en naturlig del af jeres hverdag.",
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

function PulseDot({ size = 8, color = TEAL, active = true }) {
  return (
    <span className="relative inline-flex" style={{ width: size, height: size }} aria-hidden="true">
      {active && (
        <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: color, opacity: 0.4 }} />
      )}
      <span className="relative rounded-full" style={{ width: size, height: size, backgroundColor: color }} />
    </span>
  );
}

const HERO_SIGNALS = ["Målgruppe forstået", "Tone of voice fundet", "Relevant vinkel identificeret"];
const HERO_SUGGESTIONS = [
  { Icon: Linkedin, label: "LinkedIn-opslag" },
  { Icon: Newspaper, label: "Pressehistorie" },
  { Icon: Mail, label: "Nyhedsbrev" },
  { Icon: Megaphone, label: "Kampagneidé" },
];

/* Decorative living product element — pure transform/opacity, no API/events */
function AicomLiveDemo() {
  const reduced = usePrefersReducedMotion();
  const [shown, setShown] = useState(reduced ? HERO_SIGNALS.length : 0);
  const [sug, setSug] = useState(0);

  // Build the signals up once — then never reset them
  useEffect(() => {
    if (reduced || shown >= HERO_SIGNALS.length) return;
    const id = setTimeout(() => setShown((s) => s + 1), 950);
    return () => clearTimeout(id);
  }, [shown, reduced]);

  const ready = shown >= HERO_SIGNALS.length;

  // Once analysis is done, keep rolling through the suggestions
  useEffect(() => {
    if (reduced || !ready) return;
    const id = setInterval(() => setSug((s) => (s + 1) % HERO_SUGGESTIONS.length), 1900);
    return () => clearInterval(id);
  }, [ready, reduced]);

  const signalOn = (i) => i < shown;
  const suggestion = HERO_SUGGESTIONS[sug];

  return (
    <Link
      to="/kom-i-gang"
      aria-label="Kom i gang med Aicom"
      className="block relative rounded-2xl border border-border bg-card p-5 sm:p-6 select-none cursor-pointer transition-shadow duration-300 hover:border-[#22D3C5]/60"
      style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 20px 50px -30px rgba(17,17,17,0.20)" }}
    >
      {/* header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <PulseDot active={!ready} />
          <span className="text-[13px] font-medium text-foreground">Aicom</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-border px-3 py-1">
          <span
            className="h-1.5 w-1.5 rounded-full transition-colors duration-500"
            style={{ backgroundColor: ready ? TEAL : "#C9C4BB" }}
          />
          <span className="text-[11px] tracking-wide text-muted-foreground transition-opacity duration-500">
            {ready ? "Forslag klar" : "Analyserer …"}
          </span>
        </div>
      </div>

      <p className="text-[13px] text-muted-foreground mt-5 leading-relaxed">
        Aicom lærer jeres virksomhed at kende
      </p>

      {/* signals */}
      <div className="mt-4 space-y-2">
        {HERO_SIGNALS.map((s, i) => {
          const on = signalOn(i);
          return (
            <div
              key={s}
              className="flex items-center gap-2.5 transition-all duration-500"
              style={{ opacity: on ? 1 : 0.25, transform: on ? "translateX(0)" : "translateX(-6px)" }}
            >
              <span
                className="flex h-4 w-4 items-center justify-center rounded-full border transition-colors duration-500"
                style={{
                  borderColor: on ? TEAL : "#DDD8D0",
                  backgroundColor: on ? TEAL : "transparent",
                }}
              >
                {on && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
              </span>
              <span className="text-[12.5px] text-foreground">{s}</span>
            </div>
          );
        })}
      </div>

      <div className="my-5 h-px bg-border" />

      {/* suggestion */}
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-2.5">Forslag</p>
      <div className="relative h-[52px]">
        <div
          key={reduced ? "static" : sug}
          className={`absolute inset-0 flex items-center gap-3 rounded-xl border border-border bg-secondary px-4 ${ready ? "aic-rise" : ""}`}
          style={{ opacity: ready ? 1 : 0 }}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-card border border-border">
            <suggestion.Icon className="h-4 w-4" style={{ color: TEAL }} strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-foreground leading-tight">{suggestion.label}</p>
            <p className="text-[11px] text-muted-foreground truncate">Skræddersyet til jeres profil</p>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground ml-auto shrink-0" />
        </div>
      </div>
    </Link>
  );
}

function Hero() {
  return (
    <section className="relative z-10">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-20 sm:pt-28 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-7"
            >
              <PulseDot size={6} />
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">AI-drevet kommunikation</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
              className="font-display text-4xl sm:text-5xl lg:text-[3.7rem] font-medium tracking-tight leading-[1.04] text-balance"
            >
              Din AI-drevne<br />
              <span style={{ color: TEAL }}>{"kommunikations\u00ADafdeling"}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
              className="text-muted-foreground mt-7 text-base sm:text-lg leading-relaxed max-w-xl"
            >
              Aicom kombinerer erfarne konsulenter med state of the art AI-teknologi - så vejen fra idé til resultat er kortere end nogensinde før. Vi hjælper jer med at blive synlige, relevante og interessante.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: EASE }}
              className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            >
              <MButton to="/kom-i-gang" size="lg" data-testid="hero-try-btn">
                Prøv Aicom AI gratis <ArrowRight className="h-4 w-4" />
              </MButton>
              <MButton href="#losninger" size="lg" variant="secondary" data-testid="hero-solutions-btn">
                Se løsninger
              </MButton>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
          >
            <AicomLiveDemo />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const TRUST = [
  "Bygget af erfarne kommunikationsrådgivere",
  "Udviklet til danske virksomheder",
  "Baseret på førende kommunikationsforskning",
];

function TrustLine() {
  return (
    <section className="relative z-10 border-y border-border bg-secondary/60">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {TRUST.map((t) => (
          <div key={t} className="flex items-center gap-2.5">
            <span className="flex h-4 w-4 items-center justify-center rounded-full shrink-0" style={{ backgroundColor: TEAL }}>
              <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
            </span>
            <span className="text-[13px] text-muted-foreground">{t}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturedAicom() {
  const s = SOLUTIONS_BY_SLUG["aicom-ai"];
  const chips = [
    { Icon: Lightbulb, label: "Kommunikationsidé" },
    { Icon: Linkedin, label: "LinkedIn-opslag" },
    { Icon: Newspaper, label: "Pressehistorie" },
    { Icon: Mail, label: "Nyhedsbrev" },
  ];
  return (
    <Link
      to={`/losninger/${s.slug}`}
      data-testid={`home-solution-${s.slug}`}
      className="group relative block rounded-2xl border border-border bg-card overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25"
      style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}
    >
      <span className="absolute left-0 top-0 h-full w-[3px]" style={{ backgroundColor: TEAL }} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 sm:p-10">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-5">
            <PulseDot size={7} />
            <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Aicom AI</span>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight leading-tight">
            Jeres digitale kommunikationsrådgiver
          </h3>
          <p className="text-muted-foreground mt-4 leading-relaxed text-[15px] max-w-md">
            {HOME_DESC["aicom-ai"]}
          </p>
          <span className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground">
            Oplev Aicom AI
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" style={{ color: TEAL }} />
          </span>
        </div>
        <div className="rounded-xl border border-border bg-secondary/70 p-5 sm:p-6">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-4">Producerer bl.a.</p>
          <div className="space-y-2.5">
            {chips.map(({ Icon, label }, i) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-3.5 py-2.5 transition-transform duration-300 group-hover:translate-x-0.5"
                style={{ transitionDelay: `${i * 30}ms` }}
              >
                <Icon className="h-4 w-4 shrink-0" style={{ color: TEAL }} strokeWidth={1.75} />
                <span className="text-[13px] text-foreground">{label}</span>
                <span className="ml-auto h-1.5 w-1.5 rounded-full" style={{ backgroundColor: i === 0 ? TEAL : "#DDD8D0" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}

function SolutionCard({ slug, wide = false }) {
  const s = SOLUTIONS_BY_SLUG[slug];
  return (
    <Link
      to={`/losninger/${slug}`}
      data-testid={`home-solution-${slug}`}
      className={`group flex rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25 ${
        wide ? "flex-row items-center gap-6" : "flex-col h-full"
      }`}
      style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}
    >
      <div className={wide ? "shrink-0" : "flex items-start justify-between"}>
        <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center transition-colors duration-300 group-hover:bg-foreground">
          <s.Icon className="h-5 w-5 text-foreground transition-colors duration-300 group-hover:text-background" strokeWidth={1.75} />
        </div>
        {!wide && (
          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
        )}
      </div>
      <div className={wide ? "flex-1" : "contents"}>
        <h3 className={`font-display text-lg font-medium ${wide ? "" : "mt-5"}`}>{s.navTitle}</h3>
        <p className={`text-[13.5px] text-muted-foreground mt-2 leading-relaxed ${wide ? "max-w-2xl" : "flex-1"}`}>
          {HOME_DESC[slug] || s.navDesc}
        </p>
        {!wide && (
          <span className="text-[13.5px] text-foreground mt-4 inline-flex items-center gap-1">
            Læs mere <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </span>
        )}
      </div>
      {wide && (
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </Link>
  );
}

// Three highlighted boxes directly under the featured Aicom AI card
const THREE = ["ai-avatar", "ai-agenter", "ai-implementering"];

// Remaining solutions in an editorial, asymmetric grid (all links preserved)
const GRID = [
  { slug: "paid-social", span: "lg:col-span-3" },
  { slug: "seo", span: "lg:col-span-3" },
  { slug: "thought-leadership", span: "lg:col-span-2" },
  { slug: "hjemmesider", span: "lg:col-span-2" },
  { slug: "strategi", span: "lg:col-span-2" },
];

function Solutions() {
  return (
    <section id="losninger" className="relative z-10">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: EASE }} className="max-w-2xl mb-12"
        >
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Vores løsninger</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Sætter strøm til kommunikationen</h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: EASE }} className="mb-4"
        >
          <FeaturedAicom />
        </motion.div>

        {/* Three highlighted boxes directly under the featured Aicom AI card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {THREE.map((slug, i) => (
            <motion.div
              key={slug}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.06, ease: EASE }}
            >
              <SolutionCard slug={slug} />
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
          {GRID.map((g, i) => (
            <motion.div
              key={g.slug}
              className={g.span}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.06, ease: EASE }}
            >
              <SolutionCard slug={g.slug} wide={g.wide} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FLOW = [
  { n: "01", title: "Vi forstår udfordringen", desc: "Vi sætter os ind i jeres forretning, behov og den opgave, I vil løse.", Icon: Search },
  { n: "02", title: "Vi former løsningen", desc: "Vi finder det rigtige greb og designer en løsning, der passer til jer.", Icon: Compass },
  { n: "03", title: "Vi bygger og implementerer", desc: "Vi omsætter idéen til noget, der fungerer i praksis.", Icon: Blocks },
  { n: "04", title: "Vi udvikler videre", desc: "Vi følger brugen, lærer af resultaterne og forbedrer løsningen løbende.", Icon: RefreshCw },
];

function ProcessFlow() {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setActive((a) => (a + 1) % FLOW.length), 1750);
    return () => clearInterval(id);
  }, [reduced]);

  return (
    <section className="relative z-10 border-t border-border bg-secondary/50">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: EASE }} className="max-w-2xl mb-14"
        >
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Sådan arbejder Aicom</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">
            Fra udfordring til løsning, der skaber værdi
          </h2>
          <p className="text-muted-foreground mt-5 leading-relaxed">
            Vi tager udgangspunkt i jeres forretning og bygger den løsning, der faktisk løser opgaven.
          </p>
        </motion.div>

        {/* Desktop horizontal flow */}
        <div className="hidden md:block relative">
          <div className="absolute top-5 left-0 right-0 h-px bg-border">
            <span
              className="aic-flow-dot absolute -top-[3px] h-[7px] w-[7px] rounded-full -translate-x-1/2"
              style={{ backgroundColor: TEAL, boxShadow: `0 0 0 4px ${TEAL}22` }}
            />
          </div>
          <div className="relative grid grid-cols-4 gap-6">
            {FLOW.map((f, i) => {
              const on = active === i;
              return (
                <motion.div
                  key={f.n}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.45, delay: i * 0.08, ease: EASE }}
                  className="flex flex-col items-start"
                >
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card relative z-10 transition-all duration-500"
                    style={on ? { borderColor: TEAL, backgroundColor: `${TEAL}12` } : undefined}
                  >
                    <f.Icon style={{ color: TEAL, width: 18, height: 18 }} strokeWidth={1.75} />
                  </span>
                  <p className="text-[11px] tracking-wide text-muted-foreground/70 mt-5">{f.n}</p>
                  <p className="font-display text-lg font-medium mt-1">{f.title}</p>
                  <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed max-w-[15rem]">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile vertical flow */}
        <div className="md:hidden relative pl-8">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-border" />
          <div className="space-y-8">
            {FLOW.map((f, i) => {
              const on = active === i;
              return (
                <div key={f.n} className="relative flex items-start gap-4">
                  <span
                    className="absolute -left-8 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card transition-all duration-500"
                    style={on ? { borderColor: TEAL, backgroundColor: `${TEAL}12` } : undefined}
                  >
                    <f.Icon style={{ color: TEAL, width: 18, height: 18 }} strokeWidth={1.75} />
                  </span>
                  <div className="ml-4">
                    <p className="text-[11px] tracking-wide text-muted-foreground/70">{f.n}</p>
                    <p className="font-display text-lg font-medium">{f.title}</p>
                    <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function Calculator() {
  return (
    <section className="relative z-10 border-t border-border">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0.75, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Gratis værktøj</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Find det rigtige annoncebudget</h2>
          <p className="text-muted-foreground mt-5 leading-relaxed max-w-xl">
            De fleste kampagner rammer ved siden af på grund af to klassiske fejl: Enten ser målgruppen jeres annoncer for få gange, så folk ikke når at huske brandet, eller også rammer I målgruppen for ofte, hvor de ekstra visninger alligevel ikke flytter noget og burde være brugt på nye potentielle kunder.
          </p>
          <div className="mt-8">
            <MButton to="/beregner" size="lg" data-testid="home-calculator-btn">
              Prøv beregneren <ArrowRight className="h-4 w-4" />
            </MButton>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0.75, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="rounded-2xl border border-border bg-card p-6 sm:p-8"
          style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03), 0 24px 50px -34px rgba(17,17,17,0.18)" }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <PulseDot size={6} />
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Annonceberegner</span>
            </div>
            <span className="text-[11px] text-muted-foreground/70">Eksempel</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Budget", value: "6.400", unit: "kr./md." },
              { label: "Reach", value: "20.000", unit: "personer" },
              { label: "Frekvens", value: "3,2", unit: "visninger" },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-border bg-secondary/60 p-4">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">{m.label}</p>
                <p className="font-display text-xl sm:text-2xl font-medium mt-2" style={{ color: TEAL }}>{m.value}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{m.unit}</p>
              </div>
            ))}
          </div>
          <p className="text-[13.5px] text-muted-foreground mt-6 leading-relaxed">
            Beregn på sekunder, hvad det koster at nå jeres målgruppe med den rette frekvens - eller hvor mange I kan nå med jeres budget.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative z-10 border-t border-border">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-16">
        <div className="rounded-2xl border border-border bg-card px-7 py-9 sm:px-10 sm:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Klar til næste skridt?</p>
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-balance">
              Lad os tage en uforpligtende snak
            </h2>
            <p className="text-muted-foreground mt-3 leading-relaxed max-w-lg">
              Find ud af, om jeres virksomhed er klar til at træde ind i AI-æraen.
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
            <MButton href={BOOK_HREF} size="lg" data-testid="cta-book">
              Book møde <ArrowRight className="h-4 w-4" />
            </MButton>
            <Link to="/kom-i-gang" className="text-[13.5px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1" data-testid="cta-tryfree">
              Vil I hellere se Aicom AI først? Prøv gratis
              <ArrowRight className="h-3.5 w-3.5" style={{ color: TEAL }} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />
      <Hero />
      <TrustLine />
      <Solutions />
      <ProcessFlow />
      <Calculator />
      <FinalCTA />
      <MarketingFooter />
    </div>
  );
}
